import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ProtocolProgress {
  id: string;
  user_id: string;
  protocol_id: string;
  day: number;
  completed: boolean;
  notes: string | null;
  created_at: string;
}

interface Protocol {
  id: string;
  name: string;
  description: string;
  duration_days: number;
  factor_key: string;
  expected_il_delta_min: number;
  expected_il_delta_max: number;
  daily_steps: any[];
}

interface ActiveProtocol {
  protocol: Protocol;
  progress: ProtocolProgress[];
  completedDays: number;
  currentDay: number;
  isComplete: boolean;
  streak: number;
  lastCompletedDate: string | null;
  startDate: string | null;
}

export function useProtocolProgress() {
  const queryClient = useQueryClient();

  // Fetch all protocols and user progress
  const { data: activeProtocols, isLoading } = useQuery({
    queryKey: ['user-active-protocols'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // Get all protocols
      const { data: protocols, error: protocolsError } = await supabase
        .from('protocols')
        .select('*')
        .order('name');

      if (protocolsError) throw protocolsError;

      // Get all user progress
      const { data: allProgress, error: progressError } = await supabase
        .from('protocol_progress')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (progressError) throw progressError;

      // Group progress by protocol
      const progressByProtocol = (allProgress || []).reduce((acc, p) => {
        if (!acc[p.protocol_id]) acc[p.protocol_id] = [];
        acc[p.protocol_id].push(p);
        return acc;
      }, {} as Record<string, ProtocolProgress[]>);

      // Find protocols with progress (active or completed)
      const activeList: ActiveProtocol[] = [];

      for (const protocol of protocols || []) {
        const progress = progressByProtocol[protocol.id] || [];
        if (progress.length === 0) continue;

        const completedDays = progress.filter(p => p.completed).length;
        const isComplete = completedDays >= protocol.duration_days;

        // Calculate streak
        let streak = 0;
        const sortedProgress = [...progress]
          .filter(p => p.completed)
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        if (sortedProgress.length > 0) {
          streak = 1;
          for (let i = 1; i < sortedProgress.length; i++) {
            const currentDate = new Date(sortedProgress[i - 1].created_at);
            const prevDate = new Date(sortedProgress[i].created_at);
            const diffDays = Math.floor((currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays === 1) {
              streak++;
            } else {
              break;
            }
          }
        }

        const startDate = progress.length > 0 
          ? progress.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())[0].created_at
          : null;

        const lastCompletedDate = sortedProgress.length > 0 ? sortedProgress[0].created_at : null;

        activeList.push({
          protocol: protocol as Protocol,
          progress,
          completedDays,
          currentDay: Math.min(completedDays + 1, protocol.duration_days),
          isComplete,
          streak,
          lastCompletedDate,
          startDate
        });
      }

      return activeList;
    }
  });

  // Fetch protocol achievements
  const { data: protocolAchievements } = useQuery({
    queryKey: ['protocol-achievements'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { earned: [], available: [] };

      const { data: allAchievements, error: achievementsError } = await supabase
        .from('achievements')
        .select('*')
        .eq('category', 'protocol');

      if (achievementsError) throw achievementsError;

      const { data: userAchievements, error: userError } = await supabase
        .from('user_achievements')
        .select('*')
        .eq('user_id', user.id);

      if (userError) throw userError;

      const earnedIds = new Set(userAchievements?.map(a => a.achievement_id) || []);

      return {
        earned: allAchievements?.filter(a => earnedIds.has(a.id)) || [],
        available: allAchievements?.filter(a => !earnedIds.has(a.id)) || []
      };
    }
  });

  // Check and award achievements
  const checkAchievements = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const protocols = activeProtocols || [];
      const newAchievements: string[] = [];

      // Get current achievements
      const { data: currentAchievements } = await supabase
        .from('user_achievements')
        .select('achievement_id')
        .eq('user_id', user.id);

      const earnedIds = new Set(currentAchievements?.map(a => a.achievement_id) || []);

      // Check protocol_first
      if (!earnedIds.has('protocol_first') && protocols.length > 0) {
        newAchievements.push('protocol_first');
      }

      // Check protocol_day_1
      const totalDays = protocols.reduce((sum, p) => sum + p.completedDays, 0);
      if (!earnedIds.has('protocol_day_1') && totalDays >= 1) {
        newAchievements.push('protocol_day_1');
      }

      // Check protocol_week
      if (!earnedIds.has('protocol_week') && totalDays >= 7) {
        newAchievements.push('protocol_week');
      }

      // Check protocol_complete
      const completedProtocols = protocols.filter(p => p.isComplete);
      if (!earnedIds.has('protocol_complete') && completedProtocols.length >= 1) {
        newAchievements.push('protocol_complete');
      }

      // Check protocol_multi
      if (!earnedIds.has('protocol_multi') && completedProtocols.length >= 3) {
        newAchievements.push('protocol_multi');
      }

      // Check streaks
      const maxStreak = Math.max(...protocols.map(p => p.streak), 0);
      if (!earnedIds.has('protocol_streak_3') && maxStreak >= 3) {
        newAchievements.push('protocol_streak_3');
      }
      if (!earnedIds.has('protocol_streak_7') && maxStreak >= 7) {
        newAchievements.push('protocol_streak_7');
      }

      // Check perfect protocol
      if (!earnedIds.has('protocol_perfect')) {
        const perfectProtocol = completedProtocols.find(p => {
          const expectedDays = p.protocol.duration_days;
          const uniqueDays = new Set(p.progress.filter(pr => pr.completed).map(pr => pr.day)).size;
          return uniqueDays === expectedDays;
        });
        if (perfectProtocol) {
          newAchievements.push('protocol_perfect');
        }
      }

      // Award new achievements
      for (const achievementId of newAchievements) {
        await supabase
          .from('user_achievements')
          .insert({ user_id: user.id, achievement_id: achievementId })
          .select()
          .single();
      }

      return newAchievements;
    },
    onSuccess: (newAchievements) => {
      if (newAchievements.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['protocol-achievements'] });
      }
    }
  });

  return {
    activeProtocols: activeProtocols || [],
    isLoading,
    protocolAchievements,
    checkAchievements: checkAchievements.mutate
  };
}
